import { Http } from '@mygpt/app/http';

import type { PasswordRules } from './auth';

/**
 * 安全策略（密码规则 + MFA）接口
 * 契约：安全策略前端对接文档 §0.3 / §3 / §4 / §5
 * 密码规则对象 PasswordRules 与 GET /auth/password-policy 由 ./auth 提供
 */

/** reuse_n 取值范围 */
export const REUSE_N_MIN = 1;
export const REUSE_N_MAX = 24;
/** lockout_n 取值范围 */
export const LOCKOUT_N_MIN = 1;
export const LOCKOUT_N_MAX = 10;

/** 界面初始默认值（与原型默认勾选一致，用于兜底渲染） */
export const DEFAULT_PASSWORD_RULES: PasswordRules = {
  require_case_digit: true,
  require_special: false,
  min_length_8: true,
  rotate_90d: false,
  reuse_forbidden: false,
  reuse_n: 2,
  lockout_enabled: true,
  lockout_n: 5,
  no_expiry: false,
};

/** 超管全局安全策略（GET/PUT /admin/security-policy 响应） */
export interface AdminSecurityPolicyResponse {
  /** false = 从未保存过，password_rules 为界面初始展示值，线上实际生效 legacy 行为 */
  configured: boolean;
  mfa_enabled: boolean;
  /** 当前仅 ["email"] */
  mfa_methods: string[];
  password_rules: PasswordRules;
  /** ISO8601 时间，configured=false 时为 null */
  updated_at: string | null;
  /** 操作者邮箱，configured=false 时为 null */
  updated_by: string | null;
}

/** PUT /admin/security-policy 请求（全量保存） */
export interface AdminSecurityPolicyPayload {
  mfa_enabled: boolean;
  /** 可省略，默认 ["email"] */
  mfa_methods?: string[];
  password_rules: PasswordRules;
}

/** GET /admin/security-policy/coverage 响应（生效范围横幅） */
export interface SecurityPolicyCoverage {
  users_total: number;
  users_without_company: number;
  /** true = 无企业用户数为估算口径，文案建议带「约」 */
  estimated: boolean;
  companies_custom_mfa: number;
  companies_custom_rules: number;
  /** 计算时刻（服务端缓存 1 小时），ISO8601 */
  computed_at: string;
}

/** GET /admin/security-policy/lock-status 响应 */
export interface UserLockStatus {
  email: string;
  locked: boolean;
  locked_until: string | null;
  /** 未达到锁定阈值时的当前连续失败次数 */
  fail_count: number;
}

/** 企业策略来源：继承全局默认 / 自定义覆盖 */
export type PolicySource = 'inherit' | 'custom';

/** 企业 MFA 值对象 */
export interface CompanyMfaValue {
  mfa_enabled: boolean;
  mfa_methods: string[];
}

/** GET/PUT /companies/security-policy 响应 */
export interface CompanySecurityPolicyResponse {
  company_id: string;
  mfa: {
    source: PolicySource;
    /** source=custom 时为企业自定义值，否则 null */
    custom: CompanyMfaValue | null;
    /** 全局当前值（继承态展示用），全局未配置时为 null */
    global: CompanyMfaValue | null;
    /** 实际生效值 */
    effective: CompanyMfaValue;
  };
  password_rules: {
    source: PolicySource;
    custom: PasswordRules | null;
    global: PasswordRules | null;
    /** 全局未配置且未自定义时为 null */
    effective: PasswordRules | null;
    /** true = 生效的是 legacy（全局未配置且本企业未自定义） */
    effective_legacy: boolean;
  };
}

/** PUT /companies/security-policy 请求：两个 source 必传 */
export interface CompanySecurityPolicyPayload {
  mfa_source: PolicySource;
  /** mfa_source=custom 时必传 */
  mfa_enabled?: boolean;
  /** 可省略，默认 ["email"] */
  mfa_methods?: string[];
  rule_source: PolicySource;
  /** rule_source=custom 时必传完整对象，inherit 时可省略/传 null */
  password_rules?: PasswordRules | null;
}

/**
 * 超管：获取全局安全策略
 */
export const getAdminSecurityPolicy = () => {
  const { get } = Http.use();
  return get<AdminSecurityPolicyResponse>('/admin/security-policy');
};

/**
 * 超管：全量保存全局安全策略
 * 400 NO_EXPIRY_PREREQUISITE / INVALID_RULES 由调用方按错误码提示
 */
export const putAdminSecurityPolicy = (payload: AdminSecurityPolicyPayload) => {
  const { put } = Http.use();
  return put<AdminSecurityPolicyResponse>('/admin/security-policy', payload, {
    skipErrorNotification: true,
  });
};

/**
 * 超管：获取全局策略生效范围（横幅数据，服务端缓存 1 小时）
 */
export const getSecurityPolicyCoverage = () => {
  const { get } = Http.use();
  return get<SecurityPolicyCoverage>('/admin/security-policy/coverage');
};

/** 超管：按邮箱查询密码账户的锁定状态；无密码账户返回 404 */
export const getUserLockStatus = (email: string) => {
  const { get } = Http.use();
  return get<UserLockStatus>('/admin/security-policy/lock-status', {
    params: { email },
    skipErrorNotification: true,
  });
};

/** 超管：解除密码账户锁定。接口幂等，未锁定也返回 204 */
export const unlockUserAccount = (email: string) => {
  const { post } = Http.use();
  return post<void>('/admin/security-policy/unlock', { email }, { skipErrorNotification: true });
};

/**
 * 企业：获取本企业安全策略（含 source 徽章 + 继承态灰显值 + 自定义值）
 */
export const getCompanySecurityPolicy = () => {
  const { get } = Http.use();
  return get<CompanySecurityPolicyResponse>('/companies/security-policy');
};

/**
 * 企业：保存本企业安全策略（两个 source 必传）
 * 400 NO_EXPIRY_PREREQUISITE / INVALID_RULES 由调用方按错误码提示
 */
export const putCompanySecurityPolicy = (payload: CompanySecurityPolicyPayload) => {
  const { put } = Http.use();
  return put<CompanySecurityPolicyResponse>('/companies/security-policy', payload, {
    skipErrorNotification: true,
  });
};
